import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Clock3, ChevronRight } from 'lucide-react';
import MetricCard from '../modules/dashboard/MetricCard';
import { usePermissions } from './PermissionsContext';
import { formatTimeAU } from './tenantClock';

// A module's starter Dashboard: live counts only, each one opening the
// screen (and filter) it counts. A tile shows only to Roles that can open
// its screen; a section with no tiles left doesn't show at all.
//
// A tile: { key, label, screen, count: () => Promise<number>, to, state, icon }
// `state` travels to the list as router state (e.g. { initialFilters }).

// The total from a list endpoint: its pagination total, a { count }, or
// the number of rows when it doesn't paginate.
export const countOf = (res) => {
  const body = res.data || {};
  if (body.pagination?.total != null) return body.pagination.total;
  if (body.data?.count != null) return body.data.count;
  return Array.isArray(body.data) ? body.data.length : 0;
};

// A tile counted by one GET.
export const listCount = (url, params) => () => axios.get(url, { params }).then(countOf);

function useVisible(tiles) {
  const { accessFor } = usePermissions();
  return tiles.filter((t) => accessFor(t.screen).read);
}

// Each visible tile's count, keyed by tile key; null until loaded, and
// null again for a tile whose count failed (shown as —).
function useCounts(tiles, refreshKey) {
  const [counts, setCounts] = useState({});
  const keys = tiles.map((t) => t.key).join(',');
  useEffect(() => {
    let cancelled = false;
    setCounts({});
    tiles.forEach((tile) => {
      tile.count()
        .then((n) => { if (!cancelled) setCounts((prev) => ({ ...prev, [tile.key]: n })); })
        .catch(() => { if (!cancelled) setCounts((prev) => ({ ...prev, [tile.key]: null })); });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keys, refreshKey]);
  return counts;
}

export function DashboardShell({ title, description, onRefresh, children }) {
  const [refreshedAt, setRefreshedAt] = useState(new Date());
  return (
    <div className="dashboard dashboard-page">
      <div className="dashboard-content">
        <div className="dashboard-content-inner">
          <div className="dashboard-page-header">
            <div className="dashboard-page-header-main">
              <h2 className="dashboard-page-title">{title}</h2>
              {description && <p className="dashboard-page-description">{description}</p>}
            </div>
            {onRefresh && (
              <div className="dashboard-page-controls">
                <span className="dashboard-page-meta">
                  <Clock3 size={14} /> Last refresh: {formatTimeAU(refreshedAt)}
                </span>
                <button className="dashboard-refresh-btn" type="button" onClick={() => { setRefreshedAt(new Date()); onRefresh(); }}>Refresh</button>
              </div>
            )}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

// A refresh counter for DashboardShell's Refresh button to bump.
export function useRefreshKey() {
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);
  return [refreshKey, refresh];
}

// A row of count tiles under a heading.
export function TileSection({ title, tiles, refreshKey }) {
  const navigate = useNavigate();
  const visible = useVisible(tiles);
  const counts = useCounts(visible, refreshKey);
  if (!visible.length) return null;
  return (
    <div className="metrics-section">
      <h2>{title}</h2>
      <div className="metrics-grid">
        {visible.map((tile) => (
          <MetricCard
            key={tile.key}
            title={tile.label}
            value={counts[tile.key] == null ? '—' : counts[tile.key]}
            iconName={tile.icon || tile.label}
            onClick={() => navigate(tile.to, tile.state ? { state: tile.state } : undefined)}
          />
        ))}
      </div>
    </div>
  );
}

// Things waiting on someone: only the ones with something in them show,
// most urgent first, each opening its list.
export function ActionList({ title, items, refreshKey, emptyText }) {
  const navigate = useNavigate();
  const visible = useVisible(items);
  const counts = useCounts(visible, refreshKey);
  if (!visible.length) return null;
  const loaded = visible.every((i) => i.key in counts);
  const due = visible.filter((i) => counts[i.key] > 0);
  return (
    <div className="metrics-section">
      <h2>{title}</h2>
      <div style={{ background: 'var(--bg-white)', border: '1px solid var(--border-color)', borderRadius: '0.75rem', boxShadow: 'var(--shadow-sm)', padding: 8 }}>
        {!loaded && <div className="dashboard-empty-state" style={{ padding: 8 }}>Loading…</div>}
        {loaded && due.length === 0 && <div className="dashboard-empty-state" style={{ padding: 8 }}>{emptyText}</div>}
        {due.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => navigate(item.to, item.state ? { state: item.state } : undefined)}
            title={`Open ${item.label}`}
            style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '10px 12px', background: 'none', border: 'none', borderRadius: 8, cursor: 'pointer', textAlign: 'left', fontSize: 14, color: 'var(--text-dark)' }}
          >
            <span style={{ minWidth: 36, padding: '2px 8px', borderRadius: 999, textAlign: 'center', fontWeight: 700, ...(item.urgent ? { color: '#b91c1c', background: '#fef2f2' } : { color: '#b45309', background: '#fdf1e0' }) }}>
              {counts[item.key]}
            </span>
            <span style={{ flex: 1 }}>{item.label}</span>
            <ChevronRight size={16} color="#8a94a6" />
          </button>
        ))}
      </div>
    </div>
  );
}

// Shown when a Role can see none of a dashboard's tiles.
export function useAnyVisible(...groups) {
  const { accessFor } = usePermissions();
  return groups.flat().some((t) => accessFor(t.screen).read);
}
